import React from 'react';
import { BookOpen, Briefcase, Globe, AlertCircle } from 'lucide-react';

export const WhatWeDoSection = () => {
  const programs = [
    {
      icon: BookOpen,
      title: 'Education & Professional Development',
      subtitle: 'Practical, stage-based learning designed for real life.',
      features: [
        'Monthly live workshops led by industry experts',
        'Expert-led masterclasses: business, mindset, and scaling',
        'Growing recorded learning library — all sessions accessible to members',
        'Professional and entrepreneurial tools',
        'Success Path Framework: The Visionary → The Architect → The Hustler → The Trailblazer',
        'Members enter at any stage, engage as life evolves — no pressure, no hierarchy'
      ]
    },
    {
      icon: Briefcase,
      title: 'Economic Opportunity & Pathways',
      subtitle: 'Because learning without access is not empowerment.',
      features: [
        'Business incubators with micro-grants, mentorship, and brand partnerships',
        'Annual pitch events connecting founders to angel investors and sponsors',
        'Professional referrals and remote work opportunities',
        'Strategic collaborations and partnerships',
        'Monthly goal-setting, accountability matching, and 911 Business Emergency calls',
        'Opportunities open in cycles, earned through readiness — not popularity'
      ]
    },
    {
      icon: Globe,
      title: 'Closing the Gap — Humanitarian Action',
      subtitle: 'Our flagship global campaign. March–November, 25+ countries.',
      features: [
        'Same-day hyper-local redistribution — no warehouses, no waiting, no bureaucracy',
        'City squads partner with cafés, bakeries, salons, and small businesses to collect surplus essentials and deliver same-day to women, families, and shelters',
        'Secret Letters: handwritten, wax-sealed invitations placed in public spaces, inviting businesses to redistribute and activate the UN Women\'s Empowerment Principles (WEPs)',
        'Ethical storytelling: 60% of campaign content created by women on the ground using mobile toolkits — consent-first, dignity-centered, no savior narratives',
        'WEPs activation support: helping businesses move from symbolic commitment to visible action',
        'Culminates in our Annual Global Gala & Solidarity Walk, Montreal'
      ]
    }
  ];

  return (
    <section id="what-we-do" className="py-20" style={{ backgroundColor: 'var(--white-soft)' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="lhsc-heading text-3xl md:text-5xl mb-6">
            MOST ORGANIZATIONS CHOOSE ONE LANE.<br />WE BUILT AN ECOSYSTEM.
          </h2>
          <p className="lhsc-subheading text-base" style={{ color: 'var(--slate-grey)' }}>
            Education, opportunity, and redistribution — working together, simultaneously.
          </p>
        </div>

        {/* Three Program Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {programs.map((program, index) => (
            <div
              key={index}
              className="bg-white border border-[var(--pink-dusty)] flex flex-col"
              style={{ borderTopWidth: '4px', borderTopColor: 'var(--blue-periwinkle)' }}
            >
              <div className="p-8 flex-1">
                <program.icon size={40} className="mb-4" style={{ color: 'var(--mauve-rose)' }} />
                <h3 className="lhsc-heading text-xl mb-3">{program.title}</h3>
                <p className="lhsc-body text-sm mb-6" style={{ color: 'var(--slate-grey)' }}>
                  {program.subtitle}
                </p>
                <ul className="space-y-3 lhsc-body text-sm">
                  {program.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="mr-2" style={{ color: 'var(--mauve-rose)' }}>•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Budget Transparency Callout */}
        <div 
          className="p-8 border-l-4" 
          style={{ 
            backgroundColor: 'var(--pink-blush)', 
            borderLeftColor: 'var(--mauve-rose)' 
          }}
        >
          <div className="flex items-start">
            <AlertCircle size={24} className="mr-4 flex-shrink-0 mt-1" style={{ color: 'var(--mauve-rose)' }} />
            <div className="lhsc-body">
              <p className="font-semibold mb-2" style={{ fontWeight: 400 }}>
                100% of our funding flows directly to women through humanitarian relief, business incubators, 
                and educational programming.
              </p>
              <p>
                This happens every year — with or without institutional partnerships — because our mission comes first. 
                Every dollar tracked. Every impact reported.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
